import { CalendarDays, Factory, ListTodo, Mail, ReceiptText } from "lucide-react";
import React from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { useGetV4WarehousesQuery } from "randmar-api-client";
import {
  Badge,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { PurchaseOrder } from "../../stores/document/Document";
import CommentsView from "../comments/CommentsView";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";
import PurchaseOrderPartNumberRow from "./PurchaseOrderPartNumberRow";
import CancelPurchaseOrder from "./actions/CancelPurchaseOrder";
import EditExpectedDate from "./actions/EditExpectedDate";
import GeneratePDFWrapper from "./actions/GeneratePDFWrapper";
import SendByEmailWrapper from "./actions/SendByEmailWrapper";

interface IProps {
  purchaseOrder: PurchaseOrder;
}

const PurchaseOrderView: React.FC<IProps> = ({ purchaseOrder }) => {
  const { applicationId } = useManufacturer();
  const { data: warehouses } = useGetV4WarehousesQuery();

  const getWarehouseNameFromCode = React.useCallback((code?: string) => {
    if (!code || !warehouses) {
      return "B/O";
    }
    const warehouse = warehouses.find((w) => w.WarehouseId === code);
    return warehouse?.Name || "B/O";
  }, [warehouses]);

  const partNumberCount = purchaseOrder.partNumbers?.length ?? 0;
  const linkedReceipts = purchaseOrder.receipts ?? [];
  const estimatedTotal = React.useMemo(
    () => (purchaseOrder.partNumbers ?? []).reduce((sum, item) => sum + ((item.unitPrice ?? 0) * (item.quantity ?? 0)), 0),
    [purchaseOrder.partNumbers]
  );
  const expectedArrivalIsMissing = (purchaseOrder.expectedArrivalDate ?? 0) === 0;

  return (
    <section className="space-y-6" aria-label="Purchase order details">
      <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="space-y-4">
          <header className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Purchase order</p>
              <h2 className="text-[24px] font-semibold text-slate-900">{purchaseOrder.purchaseOrderNumber || "-"}</h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <EditExpectedDate
                expectedArrivalDate={purchaseOrder.expectedArrivalDate || 0}
                purchaseOrderNumber={purchaseOrder.purchaseOrderNumber || ""}
              />
              <SendByEmailWrapper
                documentNumber={purchaseOrder.purchaseOrderNumber || ""}
                emailAddress={purchaseOrder.contactEmail || undefined}
              >
                <Button variant="outline" size="sm" className="rounded-xl">
                  Email
                </Button>
              </SendByEmailWrapper>
              <GeneratePDFWrapper documentNumber={purchaseOrder.purchaseOrderNumber || ""} action="download">
                <Button variant="outline" size="sm" className="rounded-xl">
                  Download PDF
                </Button>
              </GeneratePDFWrapper>
              <CancelPurchaseOrder purchaseOrderNumber={purchaseOrder.purchaseOrderNumber || ""}>
                <Button variant="destructive" size="sm" className="rounded-xl">
                  Cancel PO
                </Button>
              </CancelPurchaseOrder>
            </div>
          </header>

          <dl className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3" aria-label="Purchase order metadata">
            <div className="flex items-start gap-3 rounded-xl p-3 hover:bg-gray-50">
              <CalendarDays className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Purchase order date</dt>
                <dd className="text-[15px] text-slate-700">{FormatDateFromInt(purchaseOrder.purchaseOrderDate) || "-"}</dd>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl p-3 hover:bg-gray-50">
              <CalendarDays className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Expected arrival</dt>
                <dd className="text-[15px] text-slate-700">
                  {expectedArrivalIsMissing ? (
                    <Badge variant="warning" size="sm">To Be Provided</Badge>
                  ) : (
                    FormatDateFromInt(purchaseOrder.expectedArrivalDate) || "-"
                  )}
                </dd>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl p-3 hover:bg-gray-50">
              <Factory className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Location</dt>
                <dd className="text-[15px] text-slate-700">{getWarehouseNameFromCode(purchaseOrder.location)}</dd>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl p-3 hover:bg-gray-50">
              <ListTodo className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Terms</dt>
                <dd className="text-[15px] text-slate-700">{purchaseOrder.termsCode || "-"}</dd>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl p-3 hover:bg-gray-50">
              <Mail className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contact email</dt>
                <dd className="text-[15px] text-slate-700">
                  {purchaseOrder.contactEmail && purchaseOrder.contactEmail.trim() !== ""
                    ? purchaseOrder.contactEmail
                    : <span className="italic text-slate-500">Undefined</span>}
                </dd>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl p-3 hover:bg-gray-50">
              <ReceiptText className="mt-0.5 h-5 w-5 text-slate-400" aria-hidden="true" />
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Subtotal</dt>
                <dd className="text-[15px] font-medium text-slate-900">
                  <FormattedNumber {...CurrencyUtils.format} value={purchaseOrder.subTotal || 0} />
                </dd>
              </div>
            </div>
          </dl>
        </div>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[2fr,1fr]">
        <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-[20px] font-medium text-slate-900">Line items</h3>
              <p className="text-[15px] text-slate-600">Review and adjust quantities or unit pricing.</p>
            </div>
            <p className="text-sm text-slate-500">
              {partNumberCount} line{partNumberCount === 1 ? "" : "s"}
            </p>
          </div>

          <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-100">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow className="h-14 hover:bg-gray-50">
                  <TableHead className="min-w-[240px] text-xs font-semibold uppercase tracking-wide text-slate-500">Product</TableHead>
                  <TableHead className="w-[140px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Quantity</TableHead>
                  <TableHead className="w-[140px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Update</TableHead>
                  <TableHead className="w-[160px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Unit price</TableHead>
                  <TableHead className="w-[160px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Extended</TableHead>
                  <TableHead className="w-[120px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Details</TableHead>
                  <TableHead className="w-[120px] text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Remove</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(purchaseOrder.partNumbers ?? [])
                  .slice()
                  .sort((a, b) => (a.lineNumber || 0) - (b.lineNumber || 0))
                  .map((row, index) => (
                    <PurchaseOrderPartNumberRow
                      key={`${index}-POPartNumber-${row.mpn ?? row.randmarSKU ?? "unknown"}`}
                      purchaseOrderNumber={purchaseOrder.purchaseOrderNumber || ""}
                      partNumber={row}
                    />
                  ))}

                {!purchaseOrder.partNumbers?.length ? (
                  <TableRow className="h-14">
                    <TableCell colSpan={7} className="text-center text-sm text-slate-500">
                      This purchase order has no line items.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="space-y-3">
              <header className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Financials</p>
                <h3 className="text-[20px] font-medium text-slate-900">Summary</h3>
              </header>
              <dl className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Document subtotal</dt>
                  <dd className="font-medium text-slate-900">
                    <FormattedNumber {...CurrencyUtils.format} value={purchaseOrder.subTotal || 0} />
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Estimated from line items</dt>
                  <dd className="font-medium text-slate-900">
                    <FormattedNumber {...CurrencyUtils.format} value={estimatedTotal} />
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Linked receipts</dt>
                  <dd className="font-medium text-slate-900">{linkedReceipts.length}</dd>
                </div>
              </dl>
            </div>
          </Card>

          <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="space-y-3">
              <header className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">History</p>
                <h3 className="text-[20px] font-medium text-slate-900">Comments</h3>
              </header>
              <CommentsView comments={purchaseOrder.comments || ""} />
            </div>
          </Card>

          <Card className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="space-y-3">
              <header className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Receipts</p>
                <h3 className="text-[20px] font-medium text-slate-900">Linked documents</h3>
              </header>
              {linkedReceipts.length ? (
                <div className="overflow-x-auto rounded-2xl border border-gray-100">
                  <Table>
                    <TableHeader className="bg-gray-50">
                      <TableRow className="h-12 hover:bg-gray-50">
                        <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Receipt</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Date</TableHead>
                        <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {linkedReceipts.map((receipt) => (
                        <TableRow key={receipt.receiptNumber || `receipt-${receipt.receiptDate || 0}`} className="h-12 border-b border-gray-100">
                          <TableCell>
                            {receipt.receiptNumber ? (
                              <Link
                                to={Path.document(applicationId, receipt.receiptNumber)}
                                className="font-medium text-slate-900 underline-offset-4 hover:text-[color:var(--ops-accent)] hover:underline"
                              >
                                {receipt.receiptNumber}
                              </Link>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell className="text-slate-700">{FormatDateFromInt(receipt.receiptDate) || "-"}</TableCell>
                          <TableCell className="text-right font-medium text-slate-900">
                            <FormattedNumber {...CurrencyUtils.format} value={receipt.total || 0} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-slate-500">No linked receipts for this purchase order yet.</p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default PurchaseOrderView;
